import { ComponentType, lazy, Suspense, useState } from 'react';
import { GenericLoadingFallback } from '../components/Fallback/GenericLoadingFallback';

type PropsOf<C> = C extends ComponentType<infer P> ? P : never;

export type Preload = () => Promise<void>;
export type LazyComponent<P> = ComponentType<P> & { preload: Preload };

// Every lazy module in declaration order, so the idle warm-up can fetch what has not loaded yet.
const registeredPreloads: Preload[] = [];

// One retry covers a flaky network; a second failure reaches the route's error boundary,
// which reloads the page once for stale chunks and otherwise shows the error.
function loadWithRetry<M>(load: () => Promise<M>): Promise<M> {
  return load().catch(() => load());
}

type Loaded = ComponentType<Record<string, unknown>>;

/**
 * The module's component once it has arrived, from either the preload or the first render. A
 * mount that finds it here renders it directly: going through React.lazy's thenable for a module
 * that is already loaded still commits a fallback, and React then holds the content for its 300 ms
 * fallback throttle, so every preloaded landing flashed a spinner.
 */
function pick<M extends object, K extends keyof M & string>(load: () => Promise<M>, name: K) {
  const slot: { loaded?: Loaded } = {};
  const resolve = () =>
    loadWithRetry(load).then((module) => {
      slot.loaded = module[name] as unknown as Loaded;
      return { default: slot.loaded };
    });
  return { slot, resolve };
}

function register<P>(component: ComponentType<P>, resolve: () => Promise<unknown>): LazyComponent<P> {
  const lazyComponent = component as LazyComponent<P>;
  // The browser caches the module, so preloading and the first render share one download.
  lazyComponent.preload = () => resolve().then(() => undefined);
  registeredPreloads.push(lazyComponent.preload);
  return lazyComponent;
}

/**
 * Code-splits a page component. The chunk downloads on first render and the app loader
 * fills the component's own slot meanwhile, so the shell around it (navbar, module tabs)
 * stays put. `name` is the export to use; pass 'default' for default exports.
 */
export function lazyPage<M extends object, K extends keyof M & string>(
  load: () => Promise<M>,
  name: K
): LazyComponent<PropsOf<M[K]>> {
  type Props = PropsOf<M[K]>;
  const { slot, resolve } = pick(load, name);
  const Component = lazy(resolve);
  function LazyPage(props: Props) {
    // Decided once per mount: switching paths after the first load would remount the page.
    const [Direct] = useState(() => slot.loaded);
    if (Direct) {
      return <Direct {...(props as Record<string, unknown>)} />;
    }
    return (
      <Suspense fallback={<GenericLoadingFallback />}>
        <Component {...(props as Record<string, unknown>)} />
      </Suspense>
    );
  }
  LazyPage.displayName = `lazyPage(${name})`;
  return register(LazyPage, resolve);
}

/**
 * Same as lazyPage without a Suspense of its own, for shell pieces (section navs, modals)
 * whose parent already provides the fallback that fits their slot.
 */
export function lazyShell<M extends object, K extends keyof M & string>(
  load: () => Promise<M>,
  name: K
): LazyComponent<PropsOf<M[K]>> {
  type Props = PropsOf<M[K]>;
  const { slot, resolve } = pick(load, name);
  const Component = lazy(resolve);
  function LazyShell(props: Props) {
    const [Direct] = useState(() => slot.loaded);
    if (Direct) {
      return <Direct {...(props as Record<string, unknown>)} />;
    }
    return <Component {...(props as Record<string, unknown>)} />;
  }
  LazyShell.displayName = `lazyShell(${name})`;
  return register(LazyShell, resolve);
}

type Connection = { saveData?: boolean; effectiveType?: string };

export interface WarmupDeps {
  idle: (run: () => void) => void;
  connection?: Connection;
}

const SLOW_CONNECTIONS = new Set(['slow-2g', '2g']);

let warmupLoads = 0;

/** True while the warm-up is downloading; the stale-chunk reload ignores failures in that window. */
export function isWarmingUp(): boolean {
  return warmupLoads > 0;
}

/** Loads modules two at a time, waiting for an idle slot before each, so the page's own requests go first. */
export async function runWarmup(preloads: Preload[], deps: WarmupDeps): Promise<void> {
  const { saveData, effectiveType } = deps.connection ?? {};
  if (saveData || (effectiveType && SLOW_CONNECTIONS.has(effectiveType))) {
    return;
  }
  const queue = [...new Set(preloads)];
  const worker = async () => {
    for (let next = queue.shift(); next; next = queue.shift()) {
      await new Promise<void>((resolve) => deps.idle(resolve));
      warmupLoads++;
      await next()
        .catch(() => undefined)
        .finally(() => warmupLoads--);
    }
  };
  await Promise.all([worker(), worker()]);
}

/**
 * `shell` (what any page can open, like Add Deal and the ChatBot) loads first and also on data saver
 * and 2G, since nothing on screen covers its download; `rest` follows under the connection rules.
 */
export async function runWarmupPlan(shell: Preload[], rest: Preload[], deps: WarmupDeps): Promise<void> {
  await runWarmup(shell, { idle: deps.idle });
  await runWarmup(rest, deps);
}

/**
 * Downloads the lazy modules in the background after first render: `shell`, then `first` (the
 * sections a click is most likely to hit) and everything registered. Nothing renders; later clicks
 * find the chunks already cached.
 */
export function warmPages({ shell = [], first = [] }: { shell?: Preload[]; first?: Preload[] } = {}): void {
  if (import.meta.env.MODE === 'test') {
    return;
  }
  const idle: WarmupDeps['idle'] =
    typeof window.requestIdleCallback === 'function'
      ? (run) => window.requestIdleCallback(run, { timeout: 3000 })
      : (run) => window.setTimeout(run, 250);
  const connection = (navigator as Navigator & { connection?: Connection }).connection;
  void runWarmupPlan(shell, [...first, ...registeredPreloads], { idle, connection });
}
